import { useMemo, useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { oneOf, searchUpdate } from "@/lib/search";
import { useQuery } from "@/lib/convex";
import type { Id } from "@convex/_generated/dataModel";
import type { PersonActivityStatusValue } from "@convex/schemaEnums";
import {
  LuCircleAlert,
  LuCirclePause,
  LuCalendarClock,
  LuCircleCheckBig,
  LuClock3,
  LuLockKeyhole,
  LuSparkles,
} from "react-icons/lu";
import type { IconType } from "react-icons";
import {
  Badge,
  Box,
  Card,
  Flex,
  Grid,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Stack,
  Tabs,
  Text,
} from "@chakra-ui/react";
import { api } from "@convex/_generated/api";
import { statusPresentation } from "../components/activityStatus";
import { useEffectivePersonId } from "../impersonation";
import { LoadingCard } from "../components/LoadingCard";
import { PageHeader } from "../components/PageHeader";
import { isActivityOverdue } from "@/lib/activityDates";
import { MyRecord } from "./MyRecord";
import { tagColor } from "@/lib/tagColor";

type TagSnapshot = {
  tagId: Id<"activityTags">;
  name: string;
  key: string;
  color: string | null;
};

type Assignment = {
  _id: string;
  titleSnapshot: string;
  descriptionSnapshot: string;
  tagSnapshots: TagSnapshot[];
  required: boolean;
  status: PersonActivityStatusValue;
  dueDay: string | null;
  recommendedStartDay: string | null;
  opensOnDay: string | null;
};

const sections: Array<{
  key: Assignment["status"];
  title: string;
  description: string;
  icon: IconType;
  tone: string;
}> = [
    {
      key: "needs_revision",
      title: "Needs revision",
      description: "A reviewer asked for changes.",
      icon: LuCircleAlert,
      tone: `${statusPresentation("needs_revision").tone}.solid`,
    },
    {
      key: "in_progress",
      title: "In progress",
      description: "Started, not yet submitted.",
      icon: LuClock3,
      tone: `${statusPresentation("in_progress").tone}.solid`,
    },
    {
      key: "not_started",
      title: "Upcoming",
      description: "Ready to start.",
      icon: LuSparkles,
      tone: `${statusPresentation("not_started").tone}.solid`,
    },
    {
      key: "awaiting_review",
      title: "Waiting on someone else",
      description: "Work submitted for approval, or a step somebody else has to fill in.",
      icon: LuCalendarClock,
      tone: `${statusPresentation("awaiting_review").tone}.solid`,
    },
    {
      key: "blocked",
      title: "Waiting on something else",
      description: "Waiting for another activity to be finished, or for its opening date. You can already work on the steps.",
      icon: LuLockKeyhole,
      tone: `${statusPresentation("blocked").tone}.solid`,
    },
    {
      key: "completed",
      title: "Completed",
      description: "Finished.",
      icon: LuCircleCheckBig,
      tone: `${statusPresentation("completed").tone}.solid`,
    },
    {
      // Last, not first. It used to lead on the grounds that while somebody is
      // away this is the only thing true of their work -- which is right when
      // everything is paused and wrong the rest of the time, where it put a
      // band about nothing above the work they came to see. An empty section
      // no longer renders at all, so the case it was written for still leads.
      key: "paused",
      title: "Paused",
      description: "Nothing is due while you are away. Dates move when you come back.",
      icon: LuCirclePause,
      tone: `${statusPresentation("paused").tone}.solid`,
    },
  ];

// Days are "YYYY-MM-DD" (convex/days.ts). Parsed at UTC noon so no timezone
// can shunt a deadline onto the day before.
function formatDay(day: string | null) {
  if (day === null) {
    return "—";
  }

  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
  }).format(new Date(`${day}T12:00:00Z`));
}

function statusColorPalette(status: Assignment["status"]): string {
  switch (status) {
    case "archived":
      // Archived on departure or a discontinued enrollment: not completed,
      // not deleted.
      return "gray";
    case "paused":
      // Not an alarm and not a success: the person is away, and nothing of
      // theirs is late or waiting.
      return "purple";
    case "blocked":
      return "orange";
    case "not_started":
      return "cyan";
    case "in_progress":
      return "blue";
    case "awaiting_review":
      return "teal";
    case "needs_revision":
      return "red";
    case "completed":
      return "green";
    case "cancelled":
      return "gray";
  }
}

function TagBadge({ tag }: { tag: TagSnapshot }) {
  return (
    <Badge
      variant="outline"
      textTransform="uppercase"
      letterSpacing="0.18em"
      style={{ borderColor: tagColor(tag.color), color: tagColor(tag.color) }}
      title={tag.key}
    >
      {tag.name}
    </Badge>
  );
}

function SummaryCard({ label, value, detail }: { label: string; value: string; detail: string }) {
  return (
    <Card.Root borderWidth="1px" borderColor="border">
      <Card.Body gap={2} p={6}>
        <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.2em" color="fg.muted">{label}</Text>
        <Text fontSize="3xl">{value}</Text>
        <Text fontSize="sm" color="fg.muted">{detail}</Text>
      </Card.Body>
    </Card.Root>
  );
}

const ACTIVITIES_TABS = ["activities", "record"] as const;

export function validateActivitiesSearch(search: Record<string, unknown>): { tab?: (typeof ACTIVITIES_TABS)[number] } {
  return { tab: oneOf(search.tab, ACTIVITIES_TABS) };
}

export function ActivitiesPage() {
  const effectivePersonId = useEffectivePersonId();
  const assignments = useQuery(api.personActivities.listForViewer, {
    effectivePersonId: effectivePersonId as Id<"persons"> | null,
  }) as Assignment[] | undefined;
  const summary = useQuery(api.personActivities.viewerSummary, {
    effectivePersonId: effectivePersonId as Id<"persons"> | null,
  });

  // Shared with the dashboard, which asks the same question. See
  // src/lib/activityDates.ts for why this cannot live in the query.
  const isOverdue = (assignment: Assignment) => isActivityOverdue(assignment);

  // Computed here rather than in the query: overdue is a comparison with
  // today, and a Convex query is cached until its data changes -- so a count
  // computed server-side would go stale at midnight and stay stale until
  // something unrelated wrote to the table.
  const overdueCount = useMemo(
    () => (assignments ?? []).filter(isOverdue).length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [assignments],
  );

  const groupedAssignments = useMemo(() => {
    const source = assignments ?? [];
    return Object.fromEntries(
      sections.map((section) => [
        section.key,
        source.filter((assignment) => assignment.status === section.key),
      ]),
    ) as Record<Assignment["status"], Assignment[]>;
  }, [assignments]);

  // Which half to open on. Cached by the query layer, so asking here as well
  // as inside the record half costs nothing.
  const records = useQuery(api.participations.listForPerson, {
    personId: null,
    effectivePersonId: effectivePersonId as Id<"persons"> | null,
  });

  // Decided ONCE, when both queries have first answered, and never revisited:
  // somebody with no activities should not have to find the tab that concerns
  // them, and equally a tab that moved under them as a query settled would be
  // worse than one that opened on the wrong half.
  const [autoHalf, setAutoHalf] = useState<string | null>(null);
  if (autoHalf === null && assignments !== undefined && records !== undefined) {
    setAutoHalf(assignments.length === 0 && records.length > 0 ? "record" : "activities");
  }
  // A tab somebody chose is in the address (?tab=record) and wins over the
  // one picked for them.
  const chosenHalf = useSearch({ from: "/_authed/activities" }).tab;
  const half = chosenHalf ?? autoHalf;
  const navigateHere = useNavigate({ from: "/activities" });
  const setHalf = (next: string) =>
    void navigateHere({ search: { tab: oneOf(next, ACTIVITIES_TABS) }, ...searchUpdate });

  return (
    <Box minH="full" bg="bg.subtle">
      <PageHeader
        title="Events &amp; Activities"
        description="Your activities, and the events and courses you have taken part in."
        breadcrumbs={[{ label: "CeMMbase", to: "/" }, { label: "Events & Activities" }]}
        actions={
          summary
            ? (
              <HStack flexWrap="wrap" gap={3}>
                <Badge variant="outline" px={4} py={2} textTransform="uppercase" letterSpacing="0.2em">{summary.completed} completed</Badge>
                <Badge variant="outline" px={4} py={2} textTransform="uppercase" letterSpacing="0.2em">{summary.awaitingReview} awaiting review</Badge>
                <Badge variant="outline" px={4} py={2} textTransform="uppercase" letterSpacing="0.2em">{overdueCount} overdue</Badge>
              </HStack>
            )
            : null
        }
      />

      {/*
        Two halves of equal billing, not one with the other bolted underneath.
        For most of the institute the record is the ONLY half with anything in
        it -- no activities at all, and a list of workshops, conferences and
        seminars -- and stacking would leave them scrolling past an empty
        activity list to reach the part that concerns them (model §17).

        Tabs rather than columns because either may legitimately be empty, and
        an empty column beside a full one reads as something failing to load.
      */}
      <Tabs.Root
        value={half ?? "activities"}
        onValueChange={(e) => setHalf(e.value)}
        px={{ base: 6, sm: 8, lg: 10 }}
        pt={{ base: 6, lg: 8 }}
      >
        <Tabs.List>
          <Tabs.Trigger value="activities">My activities</Tabs.Trigger>
          <Tabs.Trigger value="record">My record</Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value="record" px={0} pt={6}>
          <MyRecord effectivePersonId={effectivePersonId as Id<"persons"> | null} />
        </Tabs.Content>

        <Tabs.Content value="activities" px={0} pt={6}>
      <Stack gap={8} pb={{ base: 6, lg: 8 }}>
        <SimpleGrid as="section" gap={4} columns={{ base: 1, md: 2, xl: 4 }}>
          <SummaryCard label="Assigned" value={summary?.total !== undefined ? String(summary.total) : "—"} detail="Activities assigned to you." />
          <SummaryCard label="Completed" value={summary?.completed !== undefined ? String(summary.completed) : "—"} detail="All required steps done." />
          <SummaryCard label="Waiting on someone else" value={summary?.awaitingReview !== undefined ? String(summary.awaitingReview) : "—"} detail="Waiting on a reviewer to sign, or on somebody else to fill a step in." />
          <SummaryCard label="Needs revision" value={summary?.needsRevision !== undefined ? String(summary.needsRevision) : "—"} detail="Sent back to you with changes to make." />
        </SimpleGrid>

        {assignments === undefined ? (
          <LoadingCard label="Loading activities…" />
        ) : (
          <Stack gap={8}>
            {sections.map((section) => {
              const SectionIcon = section.icon;
              const items = groupedAssignments[section.key] ?? [];

              // An empty state per section cost a heading, a sentence and a
              // dashed box each -- three bands of nothing above the work, on a
              // page whose whole job is the work.
              if (items.length === 0) {
                return null;
              }

              return (
                <Stack as="section" key={section.key} gap={4}>
                  <Flex flexWrap="wrap" align="flex-end" justify="space-between" gap={3}>
                    <Box>
                      <HStack gap={2} fontSize="sm" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.18em" color={section.tone}>
                        <Icon as={SectionIcon} boxSize={4} />
                        {section.title}
                      </HStack>
                      <Text mt={2} fontSize="sm" color="fg.muted">{section.description}</Text>
                    </Box>
                    <Badge variant="outline" textTransform="uppercase" letterSpacing="0.18em">
                      {items.length} {items.length === 1 ? "item" : "items"}
                    </Badge>
                  </Flex>

                  {(
                    <Grid
                      gap={4}
                      templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", xl: "repeat(3, 1fr)" }}
                    >
                      {items.map((assignment) => {
                        const overdue = isOverdue(assignment);
                        return (
                          // The whole card is the link. It used to carry a
                          // "Details" button and an "Open" link to the same
                          // page, plus a line of prose explaining what was
                          // behind them.
                          <Card.Root
                            key={assignment._id}
                            asChild
                            borderWidth="1px"
                            borderColor={overdue ? "red.300" : "border"}
                            _hover={{ borderColor: "blue.400" }}
                          >
                            <Link to="/activities/$personActivityId" params={{ personActivityId: assignment._id }}>
                              <Card.Body gap={3} p={4}>
                                <Heading size="md" lineHeight="tight">{assignment.titleSnapshot}</Heading>

                                {/* No status badge: the section heading above
                                    says it, and saying it again on every card
                                    was the same word twenty times down a page.
                                    Overdue is the exception -- it is not what
                                    the section says, and it is the one thing
                                    somebody must not scroll past. */}
                                <HStack flexWrap="wrap" gap={2}>
                                  {overdue ? (
                                    <Badge colorPalette="red" variant="solid">Overdue</Badge>
                                  ) : null}
                                  {assignment.required ? null : (
                                    <Badge variant="subtle" colorPalette="gray">Encouraged</Badge>
                                  )}
                                  {assignment.tagSnapshots.map((tag) => <TagBadge key={tag.tagId} tag={tag} />)}
                                </HStack>

                                {/* One line, not three bordered boxes. Dates
                                    are what people came to read, so they are
                                    the last thing on the card and read left to
                                    right in the order they happen. */}
                                <HStack
                                  flexWrap="wrap"
                                  gap={1.5}
                                  fontSize="xs"
                                  color="fg.muted"
                                  mt="auto"
                                  pt={1}
                                >
                                  {assignment.recommendedStartDay !== null ? (
                                    <Text>Start {formatDay(assignment.recommendedStartDay)}</Text>
                                  ) : null}
                                  {assignment.recommendedStartDay !== null && assignment.dueDay !== null ? (
                                    <Text aria-hidden>·</Text>
                                  ) : null}
                                  {assignment.dueDay !== null ? (
                                    <Text color={overdue ? "red.600" : undefined} fontWeight={overdue ? "semibold" : undefined}>
                                      Due {formatDay(assignment.dueDay)}
                                    </Text>
                                  ) : null}
                                  {assignment.opensOnDay !== null ? (
                                    <>
                                      <Text aria-hidden>·</Text>
                                      <Text>Opens {formatDay(assignment.opensOnDay)}</Text>
                                    </>
                                  ) : null}
                                </HStack>
                              </Card.Body>
                            </Link>
                          </Card.Root>
                        );
                      })}
                    </Grid>
                  )}
                </Stack>
              );
            })}
          </Stack>
        )}
      </Stack>
        </Tabs.Content>
      </Tabs.Root>
    </Box>
  );
}
